/**
 * Dial · Pay CTA.
 *
 * Standalone pricing tiles + Razorpay checkout entry.
 * Structure follows hisaab pay-cta.ts; restyled with Dial's
 * oxblood-maroon + brass + cream-paper design tokens.
 *
 * Tiers from PRICING.md (Issue II · MMXXVI):
 *   Pro     ₹999/yr   — genius surfaces: relationship graph, voice memos, gift log, circle tags
 *   Bundle  ₹2,999/yr — all 8 empire apps · single user
 *
 * notes: { brand: 'dial', tier } — multi-brand attribution on shared Razorpay account.
 */
import { checkout, TIER, priceLabel } from '../chassis/payment.ts';

type PayTier = 'pro' | 'family';

const TIERS: { id: PayTier; label: string; recommended?: true; desc: string }[] = [
  {
    id: 'pro',
    label: 'Dial Pro',
    recommended: true,
    desc: 'Relationship-health graph · voice memos · gift log · circle tags. 1 device.',
  },
  {
    id: 'family',
    label: 'Empire Bundle',
    desc: 'All 8 empire apps · single user · priority WhatsApp support.',
  },
];

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 40px 0 20px;
  position: relative;
}
.shell {
  background: linear-gradient(180deg, var(--paper-card, oklch(97% 0.02 75)) 0%, var(--paper-up, oklch(96% 0.025 75)) 100%);
  border: 1px solid var(--maroon, oklch(30% 0.13 22));
  padding: 26px 22px 22px;
  border-radius: 2px;
  position: relative;
}
.shell::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid color-mix(in oklch, var(--brass, oklch(68% 0.11 78)) 22%, transparent);
  border-radius: 2px;
  pointer-events: none;
}
.head { text-align: center; margin-bottom: 22px; }
.eyebrow {
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--ink-faint, oklch(48% 0.06 25 / 0.55));
}
h3 {
  font-family: var(--display, serif);
  font-size: 26px;
  font-style: italic;
  font-weight: 600;
  margin: 6px 0 4px;
  color: var(--maroon, oklch(30% 0.13 22));
}
.tagline {
  font-family: var(--display, serif);
  font-size: 14px;
  color: var(--ink-dim, oklch(40% 0.08 24 / 0.88));
}
.tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.tier {
  background: var(--paper-card, oklch(97% 0.02 75));
  border: 1px solid var(--maroon, oklch(30% 0.13 22));
  padding: 18px 14px;
  border-radius: 2px;
  display: grid;
  gap: 8px;
  cursor: pointer;
  transition: transform 100ms;
  position: relative;
}
.tier:hover { transform: translateY(-2px); }
.tier.recommended { border-color: var(--maroon-bright, oklch(38% 0.17 22)); border-left: 3px solid var(--maroon, oklch(30% 0.13 22)); }
.tier.recommended::after {
  content: 'recommended';
  position: absolute;
  top: -10px;
  left: 12px;
  background: var(--maroon, oklch(30% 0.13 22));
  color: var(--paper, oklch(94% 0.03 70));
  font-family: var(--mono, monospace);
  font-size: 8.5px;
  padding: 3px 8px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
}
.label {
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-dim, oklch(40% 0.08 24 / 0.88));
}
.price {
  font-family: var(--display, serif);
  font-size: 28px;
  font-style: italic;
  color: var(--maroon, oklch(30% 0.13 22));
  font-weight: 700;
}
.per {
  font-family: var(--mono, monospace);
  font-size: 9.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint, oklch(48% 0.06 25 / 0.55));
}
.desc {
  font-family: var(--display, serif);
  font-size: 13px;
  color: var(--ink, oklch(24% 0.09 24));
}
button {
  appearance: none;
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 10px;
  background: linear-gradient(180deg, var(--maroon, oklch(30% 0.13 22)), var(--maroon-deep, oklch(24% 0.13 18)));
  color: var(--paper, oklch(94% 0.03 70));
  border: none;
  border-radius: 2px;
  cursor: pointer;
  margin-top: 6px;
}
button:hover { filter: brightness(1.1); }
.footer {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed color-mix(in oklch, var(--brass, oklch(68% 0.11 78)) 40%, transparent);
  font-family: var(--mono, monospace);
  font-size: 9.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-align: center;
  color: var(--ink-faint, oklch(48% 0.06 25 / 0.55));
}
.success {
  margin-top: 16px;
  padding: 12px;
  background: color-mix(in oklch, var(--brass-soft, oklch(82% 0.08 80)) 35%, transparent);
  border-left: 3px solid var(--brass-deep, oklch(58% 0.13 75));
  font-family: var(--display, serif);
  font-size: 14px;
  display: none;
}
.success.show { display: block; }
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Membership · MMXXVI</div>
    <h3>Apna inner circle, pro kar.</h3>
    <div class="tagline">Relationship graph, voice memos, gift log. Jo dikhta nahi woh matter karta hai.</div>
  </div>
  <div class="tiers" id="tier-grid"></div>
  <div class="success" id="success"></div>
  <div class="footer">Razorpay · 30-day refund · GST e-invoice · DPDP-tight</div>
</section>
`;

export class DialPayCta extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const grid = this.#shadow.getElementById('tier-grid') as HTMLDivElement;
    const success = this.#shadow.getElementById('success') as HTMLDivElement;

    TIERS.forEach((t) => {
      const paise = TIER[t.id]?.amount_paise ?? 0;
      const priceStr = t.id === 'pro' ? '₹999' : '₹2,999';
      const card = document.createElement('div');
      card.className = `tier${t.recommended ? ' recommended' : ''}`;
      card.innerHTML = `
        <div class="label">${t.label}</div>
        <div class="price">${priceStr}</div>
        <div class="per">per year</div>
        <div class="desc">${t.desc}</div>
        <button data-tier="${t.id}">Buy ${t.label.split(' ')[0]}</button>
      `;
      grid.appendChild(card);
      void paise; // typecheck ref
    });

    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-tier]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset.tier as PayTier;
        if (!tier) return;
        try {
          const result = await checkout(tier, {});
          if (!result.dismissed && result.paymentId) {
            success.textContent = `Payment captured · id ${result.paymentId}. Activation email going to wickedrita@gmail.com.`;
            success.classList.add('show');
            localStorage.setItem(`dial:tier`, tier);
            localStorage.setItem(`dial:payment_id`, result.paymentId);
          }
        } catch (e) {
          success.textContent = `Checkout failed · ${e instanceof Error ? e.message : 'unknown'}. Email aditya@taxwalaai.com.`;
          success.classList.add('show');
        }
      });
    });

    void priceLabel; // typecheck ref
  }
}

customElements.define('dial-pay-cta', DialPayCta);
